import { Box, Divider, useTheme } from "@mui/material";
import { Link, Outlet, useLocation } from "react-router-dom";

import {
  PageLayout,
  RobotoStyledTab,
  RobotoStyledTabs,
} from "@/shared/components";

import { AgentTab, useActiveAgentTab } from "./useActiveAgentTabFromUrl";

/**
 * Top-level Agents page: parent layout for the `/agents/*` route tree.
 * Renders the page chrome (header, tab strip) and delegates the active
 * tab's body to `<Outlet>`.
 *
 * The active tab is derived from the trailing URL segment by
 * `useActiveAgentTab` so deep links into a specific tab survive refresh,
 * and breadcrumbs from descendant pages can target a named tab
 * (`/agents/sessions`) rather than an index that may redirect elsewhere
 * once additional tabs land.
 *
 * Layout note: the page height flows through a flex chain (page-content →
 * outer Box → tab body Box → outlet) so an inner ``DataTable`` can take
 * the remaining vertical space, matching the Datasets / Events tables.
 */
export function AgentsTabs() {
  const theme = useTheme();
  const [activeTab] = useActiveAgentTab();
  const location = useLocation();

  return (
    <PageLayout title={"Roboto - Agents"} headerContentLeft={<>Agents</>}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <div>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <RobotoStyledTabs value={activeTab} aria-label="agents tabs">
              <RobotoStyledTab
                value={AgentTab.Sessions}
                label="Sessions"
                component={Link}
                to={{
                  pathname: AgentTab.Sessions,
                  search: location.search,
                }}
                replace
              />
            </RobotoStyledTabs>
          </Box>
          <Divider sx={{ borderColor: theme.border.color }} />
        </div>

        <Box
          sx={{
            // ``flex: 1`` + ``minHeight: 0`` together let the tab body fill
            // the remaining page-content height while still letting an
            // inner ``DataTable`` scroll its own overflow. Without
            // ``minHeight: 0`` the flex item refuses to shrink below its
            // content size and the inner ``height: 100%`` chain collapses.
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: theme.spacing(2, 0),
            paddingBottom: theme.spacing(1),
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </PageLayout>
  );
}
